import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["quantityInput", "subtotal", "progressBar", "shippableValue", "freeShippingValue", "progressText", "productRow"]
  static values = {
    freeShipping: Number,
    baseValue: Number
  }

  connect() {
    this.updateAll()
  }

  increment(event) {
    const row = event.target.closest("[data-upsell-target='productRow']")
    if (!row) return
    const input = row.querySelector("[data-upsell-target='quantityInput']")
    const val = parseInt(input.value, 10) || 0
    const max = this._parseMaxQuantity(input)
    const nextVal = val + 1
    if (max != null && nextVal > max) return
    input.value = nextVal
    input.dispatchEvent(new Event("change", { bubbles: true }))
    this.updateRow(row)
    this.updateProgressBar()
  }

  decrement(event) {
    const row = event.target.closest("[data-upsell-target='productRow']")
    if (!row) return
    const input = row.querySelector("[data-upsell-target='quantityInput']")
    const val = parseInt(input.value, 10) || 0
    if (val > 0) {
      input.value = val - 1
      input.dispatchEvent(new Event("change", { bubbles: true }))
      this.updateRow(row)
      this.updateProgressBar()
    }
  }

  quantityChanged(event) {
    const row = event.target.closest("[data-upsell-target='productRow']")
    let val = parseInt(event.target.value, 10)
    if (isNaN(val) || val < 0) val = 0
    const max = this._parseMaxQuantity(event.target)
    if (max != null && val > max) val = max
    event.target.value = val
    this.updateRow(row)
    this.updateProgressBar()
  }

  addToCartStandalone(event) {
    const row = event.target.closest("[data-upsell-target='productRow']")
    if (!row) return
    const input = row.querySelector("[data-upsell-target='quantityInput']")
    if (!input) return
    const max = this._parseMaxQuantity(input)
    if (max != null && max < 1) return
    input.value = 1
    input.dispatchEvent(new Event("change", { bubbles: true }))
    this.updateRow(row)
    this.updateProgressBar()
  }

  updateRow(row) {
    const input = row.querySelector("[data-upsell-target='quantityInput']")
    const price = parseFloat(row.dataset.upsellPriceValue || 0)
    const qty = parseInt(input?.value, 10) || 0
    const subtotal = (price * qty).toFixed(2)
    const subtotalEl = row.querySelector("[data-upsell-target='subtotal']")
    if (subtotalEl) subtotalEl.textContent = `$${subtotal}`

    const addToCartBtn = row.querySelector("[data-upsell-standalone-add-to-cart]")
    const quantitySection = row.querySelector("[data-upsell-standalone-quantity]")
    if (addToCartBtn && quantitySection) {
      addToCartBtn.classList.toggle("hidden", qty > 0)
      quantitySection.classList.toggle("hidden", qty <= 0)
    }
  }

  updateAll() {
    this.productRowTargets.forEach(row => this.updateRow(row))
    this.updateProgressBar()
  }

  updateProgressBar() {
    const baseValue = this.baseValueValue || 0
    let upsellTotal = 0
    this.productRowTargets.forEach(row => {
      const input = row.querySelector("[data-upsell-target='quantityInput']")
      const price = parseFloat(row.dataset.upsellPriceValue || 0)
      const qty = parseInt(input?.value, 10) || 0
      upsellTotal += price * qty
    })
    const totalValue = baseValue + upsellTotal
    const freeShipping = this.freeShippingValue || 250

    if (this.hasShippableValueTarget) {
      this.shippableValueTarget.textContent = this.formatCurrency(totalValue)
    }
    if (this.hasProgressBarTarget) {
      const remaining = Math.max(0, freeShipping - totalValue)
      const style = totalValue >= freeShipping
        ? "grid-template-columns: 100fr 0fr;"
        : `grid-template-columns: ${totalValue}fr ${remaining}fr;`
      this.progressBarTarget.style.cssText = style
    }
    if (this.hasProgressTextTarget) {
      if (totalValue >= freeShipping) {
        this.progressTextTarget.innerHTML = "Congratulations you qualify for <strong class='font-semibold'>Free Shipping!</strong>"
      } else {
        const remaining = (freeShipping - totalValue).toFixed(2)
        this.progressTextTarget.innerHTML = `You're only <strong class='font-semibold'>$${remaining}</strong> away from <strong class='font-semibold'>Free Shipping!</strong>`
      }
    }
  }

  formatCurrency(val) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val)
  }

  /** @returns {number|null} */
  _parseMaxQuantity(input) {
    if (!input?.getAttribute) return null
    const raw = input.getAttribute("max")
    if (raw === null || raw === "") return null
    const n = parseInt(raw, 10)
    return Number.isNaN(n) ? null : n
  }
}
